import {
  CUSTOM_ELEMENTS_SCHEMA,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  LOCALE_ID,
  NgModule,
} from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { SharedModule } from './shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgrxModule } from './core/store/ngrx.module';
import { ManagerLayoutComponent } from './layout/manager-layout/manager-layout.component';
import { SidebarLinksComponent } from './layout/sidebar-links/sidebar-links.component';
import { HomePageComponent } from './public/pages/home-page/home-page.component';
import { VendorPageComponent } from './public/pages/vendor-page/vendor-page.component';
import { FeatureComponent } from './public/components/feature.component';
import { RoomPageComponent } from './public/pages/room-page/room-page.component';
import localeFr from '@angular/common/locales/fr-CH';
import localeDe from '@angular/common/locales/de-CH';
import {
  IMAGE_CONFIG,
  IMAGE_LOADER,
  ImageLoaderConfig,
  NgOptimizedImage,
  registerLocaleData,
} from '@angular/common';
import { FooterComponent } from './layout/footer/footer.component';
import { PublicNavbarLinksComponent } from './layout/public-navbar-links/public-navbar-links.component';
import { NavbarComponent } from './layout/navbar/navbar.component';
import { BookingPageComponent } from './public/pages/booking-page/booking-page.component';
import { BookingConfirmationPageComponent } from './public/pages/booking-confirmation-page/booking-confirmation-page.component';
import { MyBookingPage } from './public/pages/booking-detail-page/my-booking-page.component';
import { CustomerInvoicesTableComponent } from './public/pages/booking-detail-page/components/customer-invoices-table.component';
import { CustomerPaymentsTableComponent } from './public/pages/booking-detail-page/components/customer-payments-table.component';
import { BookingCancellationSectionComponent } from './public/pages/booking-detail-page/components/booking-cancellation-section.component';
import { PermissionsPageComponent } from './public/pages/permissions-page/permissions-page.component';
import { GeneralTermsPageComponent } from './public/pages/general-terms-page/general-terms-page.component';
import { PrivacyPolicyPageComponent } from './public/pages/privacy-policy-page/privacy-policy-page.component';
import { PricingPageComponent } from './public/pages/pricing-page/pricing-page.component';
import { NotFoundPageComponent } from './core/pages/not-found-page/not-found-page.component';
import { VendorPrivacyPolicyPageComponent } from './public/pages/vendor-privacy-policy-page/vendor-privacy-policy-page.component';
import { ChangelogModule } from './changelog/changelog.module';
import { CookieService } from 'ngx-cookie-service';
import { UnlockDoorPageComponent } from './public/pages/unlock-door/unlock-door-page.component';
import { AboutPageComponent } from './public/pages/about-page.component';
import { PartnersResellersPageComponent } from './public/pages/partners-resellers-page/partners-resellers-page.component';
import { PublicInfoCardComponent } from './public/components/public-info-card.component';
import { HeroFormulaCardComponent } from './public/components/hero-formula-card.component';
import { PublicTagComponent } from './public/components/public-tag.component';
import { PageSectionComponent } from './shared/components/page-section.component';
import { FeatureSectionComponent } from './public/components/feature-section.component';
import { environment } from '../environments/environment';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { CouponRedeemComponent } from './public/components/coupon-redeem.component';
import { SidebarSubtitleComponent } from './layout/sidebar-subtitle/sidebar-subtitle.component';
import { SidebarLinkComponent } from './layout/sidebar-link/sidebar-link.component';
import { SidebarSectionTitleComponent } from './layout/sidebar-section-title/sidebar-section-title.component';
import { TitleCasePipe } from './core/pipes/titleCase.pipe';
import { TitleStrategy } from '@angular/router';
import { AppTitleStrategy } from './core/strategies/app-title.strategy';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { FeatureDirective } from './shared/directives/feature.directive';
import { DropdownMenuComponent } from './layout/dropdown-menu/dropdown-menu.component';
import { ContactPageComponent } from './public/pages/contact-page/contact-page.component';
import { VendorsListPageComponent } from './public/pages/vendors-list-page/vendors-list-page.component';
import { VendorCardComponent } from './public/components/vendor-card/vendor-card.component';
import { ContactCardComponent } from './public/pages/contact-page/contact-card/contact-card.component';
import { TooltipDirective } from './shared/directives/tooltip.directive';
import { BookingsModule } from './bookings/bookings.module';

registerLocaleData(localeFr, 'fr-CH');
registerLocaleData(localeDe, 'de-CH');

@NgModule({
  declarations: [
    AppComponent,
    ManagerLayoutComponent,
    SidebarLinksComponent,
    HomePageComponent,
    VendorPageComponent,
    FeatureComponent,
    RoomPageComponent,
    FooterComponent,
    NavbarComponent,
    PublicNavbarLinksComponent,
    BookingPageComponent,
    BookingConfirmationPageComponent,
    MyBookingPage,
    CustomerInvoicesTableComponent,
    CustomerPaymentsTableComponent,
    BookingCancellationSectionComponent,
    PermissionsPageComponent,
    GeneralTermsPageComponent,
    PrivacyPolicyPageComponent,
    PricingPageComponent,
    NotFoundPageComponent,
    VendorPrivacyPolicyPageComponent,
    UnlockDoorPageComponent,
    AboutPageComponent,
    PartnersResellersPageComponent,
    PublicInfoCardComponent,
    HeroFormulaCardComponent,
    ContactPageComponent,
    VendorsListPageComponent,
    SidebarSubtitleComponent,
    SidebarLinkComponent,
    SidebarSectionTitleComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    BrowserAnimationsModule,
    NgrxModule,
    ChangelogModule,
    PublicTagComponent,
    PageSectionComponent,
    FeatureSectionComponent,
    NgOptimizedImage,
    TitleCasePipe,
    FeatureDirective,
    DropdownMenuComponent,
    VendorCardComponent,
    ContactCardComponent,
    TooltipDirective,
    BookingsModule,
  ],
  providers: [
    ...(environment.production
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
              showDialog: false,
            }),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
        ]
      : []),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'fr-CH',
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'CHF',
    },
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: 'https://api.ercos.ch/js/tinymce/tinymce.min.js',
    },
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => {
        let url = `${environment.adminUrl}/images/${config.src}?`;
        let queryParams = ['fm=webp'];
        if (config.width) {
          queryParams.push(`w=${config.width}`);
        }

        return url + queryParams.join('&');
      },
    },
    {
      provide: IMAGE_CONFIG,
      useValue: {
        breakpoints: [384, 750, 1080, 1200, 1920], // valeurs utilisées pour les srcset automatiquement
      },
    },
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    CookieService,
    provideClientHydration(withEventReplay()),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAnimationsAsync(),
  ],
  exports: [CouponRedeemComponent],
})
export class AppModule {}
